import { TWorkflowExecution } from '@commenda/types';
import {
  ServiceRequestDetails,
  TaskGroupWithSLA,
  SLAStatus,
} from '@commenda/types/src/service-request/types';
import { TSrPageTask } from '@commenda/types/src/service-request/types';
import { CorporationType, Country, TaskParty, TaskStatus, WorkRequest } from '@prisma/client';
import { KeyedMutator } from 'swr';

export enum ServiceRequestTaskCardDisplayVariant {
  COMPLIANCE = 'compliance',
  COMPLIANCE_TASK_PREVIEW = 'compliance-task-preview',
  SALES_TAX_FILING_TASK_PREVIEW = 'sales-tax-filing-task-preview',
  ONLY_ACTION_BUTTON = 'only-action-button',
  DEFAULT = 'default',
}

export type TAssignedUserSummary = {
  id: string;
  name: string | null;
  email: string | null;
  image: string | null;
};

export type TServiceRequestTask = (TSrPageTask & {
  calculatedSLA?: SLAStatus;
}) & {
  workRequest: WorkRequest;
  AssignedUser: TAssignedUserSummary | null;
  taskGroup: TaskGroupWithSLA & {
    tasks: TSrPageTask[];
  };
};

export type TServiceRequestContext = {
  serviceRequest: ServiceRequestDetails;
  role: TaskParty;
  mutate: KeyedMutator<unknown>;
  variant: ServiceRequestTaskCardDisplayVariant;
  workflowExecution?: TWorkflowExecution;
};

export type TCommonTaskProp = {
  context: TServiceRequestContext;
  task: TServiceRequestTask;
  isTimelineView?: boolean; // For the task view only, we don't want to stroke or the task number. Only relevant for non-timeline view. Defaults to true if not provided.
  displayNumber?: number;
};

export type ServiceRequestTaskCardConfig = {
  // The reason why we have a function is because we might have some task with custom icons based on the status.
  getLeftIcon: (status: TaskStatus) => React.ReactNode;
  getStateConfig: (state: {
    role: TaskParty;
    status: TaskStatus;
  }) => {
    actions: React.ReactNode;
    label: React.ReactNode;
    // Optional overrides for the timeline card; falls back to `task.title`
    // / `stepDefinition.description` when omitted. Used by step types that
    // render state-dependent copy at runtime (e.g. SYS_ROOTFI_SYNC).
    title?: React.ReactNode;
    description?: React.ReactNode;
  };
};

export type ServiceRequestTaskProps = {
  task: TServiceRequestTask;
  config: ServiceRequestTaskCardConfig;
  calculatedSLA?: SLAStatus;
  serviceRequestId?: string;
  state: {
    role: TaskParty; // Used to determine ENV, i.e. AGENT, CLIENT or ADMIN
    status: TaskStatus;
  };
  context: TServiceRequestContext;
  isTimelineView?: boolean; // For the task view only, we don't want to stroke or the task number. Only relevant for non-timeline view. Defaults to true if not provided.
  displayNumber?: number;
};

export type EmailTemplate = {
  subject: string;
  body: string;
};

export type TAddBankDetailsTaskMetadata = {
  bankAccountId: string;
};

export type TLinkBankAccountsTaskMetadata = {
  bankAccounts?: string[];
};

export type TIncorporationInfoTaskMetadata = {
  incorporationInfo?: {
    corporationName?: string;
    formationDate?: string;
    uniqueIdentifierFromJurisdiction?: string;
    registeredAgent?: {
      id?: string;
      address?: string;
    };
    authorizedShares?: number;
  };
};

export type TDSCProcurementTaskMetadata = {
  directorId?: number;
  directorDetails?: {
    directorFirstName: string;
    directorMiddleName?: string;
    directorLastName: string;
    number: string;
    email: string;
    personalDetails?: {
      nationality?: Country;
      panCardNumber?: string;
      aadharCardNumber?: string;
      passportNumber?: string;
      passportCountryOfIssue?: Country;
    };
  };
};

export type TAdditionalTask = {
  title: string;
  service: string;
  entity: {
    id: number;
    companyLegalName: string;
    incorporationCountry: Country;
    corporationType: CorporationType;
  } | null;
  dueDate: string;
  assignedTo: [];
  link: string | null;
};

export enum ServiceRequestTaskGroupView {
  TASKS = 'tasks',
  TIMELINE = 'timeline',
}
